import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie'; // ✅ Import js-cookie
import { extractDataArray } from './Constants';

const Eventtickets = () => {
  const [participants, setParticipants] = useState([]);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [participantSearchText, setParticipantSearchText] = useState('');
  const [sortColumn, setSortColumn] = useState('booking_time');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const userCollege = Cookies.get('college') || 'main';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Aliases to handle different spellings or acronyms stored in the database
    const aliases = {
      'hrc': ['hrc', 'hansraj'],
      'rjc': ['rjc', 'ramjas'],
      'jcbust': ['jcbust', 'jc bose', 'j.c. bose', 'jcbose', 'jc bose ust'],
      'clc': ['clc', 'campus law'],
      'nitp': ['nitp', 'nit patna'],
      'cu': ['cu', 'chandigarh university'],
      'pu': ['pu', 'panjab university', 'punjab'],
      'lpu': ['lpu', 'lovely professional'],
      'fot': ['fot', 'faculty of technology'],
      'cic': ['cic', 'cluster innovation'],
      'kmc': ['kmc', 'kirorimal', 'kirori mal'],
      'mhc': ['mhc', 'miranda house'],
      'drc': ['drc', 'daulat ram']
    };

    const adminCollege = String(userCollege).toLowerCase();
    const validTerms = aliases[adminCollege] || [adminCollege];

    const filtered = events.filter((event) => {
      // 1. Check if the user is allowed to see this event
      let isAllowed = false;
      if (userCollege === 'main') {
        isAllowed = true; // Main admin sees all events
      } else {
        // Upcoming events store the college in event.college
        const eventCollege = String(event.college || '').toLowerCase();
        isAllowed = validTerms.some(term => 
          term.length <= 2 ? eventCollege.split(/[\s,]+/).includes(term) : eventCollege.includes(term)
        );
      }

      if (!isAllowed) return false;

      // 2. Check if it matches the search text
      return event.name && event.name.toLowerCase().includes(searchText.toLowerCase());
    });

    setFilteredEvents(filtered);
  }, [searchText, events]);

  useEffect(() => {
    if (selectedEventId) {
      let filtered = participants
        .filter((p) => String(p.event_id) === String(selectedEventId))
        .filter((p) => {
          const nameMatch = p.name && p.name.toLowerCase().includes(participantSearchText.toLowerCase());
          const emailMatch = p.email && p.email.toLowerCase().includes(participantSearchText.toLowerCase());
          const phoneMatch = p.phone && p.phone.toLowerCase().includes(participantSearchText.toLowerCase());
          const idMatch = p.id && p.id.toString().includes(participantSearchText);
          return nameMatch || emailMatch || phoneMatch || idMatch;
        });

      filtered = sortData(filtered, sortColumn, sortOrder);

      setFilteredParticipants(filtered);
    }
  }, [participantSearchText, selectedEventId, participants, sortColumn, sortOrder]);

  const fetchData = async () => {
    try {
      const participantsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/event_bookings/read`);
      const participantsData = await participantsRes.json();
      setParticipants(extractDataArray(participantsData));

      const eventsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upcoming_events/read`);
      const eventsData = await eventsRes.json();
      setEvents(extractDataArray(eventsData));
      // Removed setFilteredEvents(eventsData) here because the useEffect handles the initial filtering automatically
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  const getValidColumns = (data) => {
    if (!data || data.length === 0) return [];

    const keys = Object.keys(data[0]);

    return keys.filter((key) =>
      data.some(
        (item) =>
          item[key] !== null &&
          item[key] !== undefined &&
          item[key] !== ''
      )
    );
  };

  const sortData = (data, column, order) => {
    return [...data].sort((a, b) => {
      let valA = a[column] ?? '';
      let valB = b[column] ?? '';

      // Convert numbers
      if (!isNaN(valA) && !isNaN(valB)) {
        valA = Number(valA);
        valB = Number(valB);
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const handleDownloadExcel = (eventId) => {
    const selected = participants.filter(p => String(p.event_id) === String(eventId));

    if (selected.length === 0) {
      Swal.fire({ icon: 'info', title: 'No Data', text: 'There is no data to download.' });
      return;
    }

    Swal.fire({
      title: 'Generating Excel...',
      text: 'Please wait while the file is being prepared.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    setTimeout(() => {
      try {
        const allKeys = Array.from(
          selected.reduce((keys, item) => {
            Object.keys(item).forEach(key => keys.add(key));
            return keys;
          }, new Set())
        );

        const cleanedData = selected.map(item => {
          const row = {};
          allKeys.forEach(key => {
            row[key] = item[key];
          });
          return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(cleanedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Participants');
        XLSX.writeFile(workbook, `Participants_Event_${eventId}.xlsx`);

        Swal.fire({ icon: 'success', title: 'Download Ready', text: 'Excel file downloaded successfully.' });
      } catch (error) {
        console.error('Download error:', error);
        Swal.fire({ icon: 'error', title: 'Download Failed', text: 'Error while creating the Excel file.' });
      }
    }, 500);
  };

  const handleViewListClick = (eventId) => {
    setSelectedEventId(eventId);
    const filtered = participants.filter((p) => String(p.event_id) === String(eventId));
    setFilteredParticipants(filtered);
    setParticipantSearchText('');
  };

  const handleBack = () => {
    setSelectedEventId(null);
    setFilteredParticipants([]);
    setSelectedParticipants([]);
  };

  const handleApproveCertificate = async (bookingId) => {
    const result = await Swal.fire({
      title: 'Approve Certificate?',
      text: `This will approve the certificate for booking #${bookingId}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/event_bookings/approve_certificate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ booking_id: bookingId }),
        }
      );
      const data = await res.json();
      if (res.ok && (data.success || data.status === 'success')) {
        Swal.fire({ icon: 'success', title: 'Approved!', text: 'Certificate approved successfully.' });
        // Update local state so the button changes without a full reload
        setParticipants(prev =>
          prev.map(p => String(p.id) === String(bookingId) ? { ...p, certificate_approved: '1' } : p)
        );
        setFilteredParticipants(prev =>
          prev.map(p => String(p.id) === String(bookingId) ? { ...p, certificate_approved: '1' } : p)
        );
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: data.error || data.message || 'Could not approve certificate.' });
      }
    } catch (err) {
      console.error('Approve error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Network error. Please try again.' });
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const selectable = filteredParticipants.filter(p => String(p.certificate_approved) !== '1').map(p => p.id);
      setSelectedParticipants(selectable);
    } else {
      setSelectedParticipants([]);
    }
  };

  const handleSelectParticipant = (id) => {
    setSelectedParticipants(prev => 
      prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id]
    );
  };

  const handleBulkApprove = async () => {
    if (selectedParticipants.length === 0) return;

    const result = await Swal.fire({
      title: 'Approve Selected Certificates?',
      text: `This will approve certificates for ${selectedParticipants.length} participant(s).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve All',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/event_bookings/approve_bulk_certificates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ booking_ids: selectedParticipants }),
        }
      );
      const data = await res.json();
      if (res.ok && (data.success || data.status === 'success')) {
        Swal.fire({ icon: 'success', title: 'Approved!', text: 'Certificates approved successfully.' });
        
        setParticipants(prev =>
          prev.map(p => selectedParticipants.includes(p.id) ? { ...p, certificate_approved: '1' } : p)
        );
        setFilteredParticipants(prev =>
          prev.map(p => selectedParticipants.includes(p.id) ? { ...p, certificate_approved: '1' } : p)
        );
        setSelectedParticipants([]);
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: data.error || data.message || 'Could not approve certificates.' });
      }
    } catch (err) {
      console.error('Bulk Approve error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Network error. Please try again.' });
    }
  };

  const validColumns = getValidColumns(filteredParticipants);
  // Columns to hide from the auto-generated table (we handle certificate_approved manually)
  const displayColumns = validColumns.filter(k => k !== 'certificate_approved');

  return (
    <div className="main-activities-container">
      <div className="premium-report-card">

        {!selectedEventId ? (
          <>
            <div className="premium-report-header">
              <h1 className="header-title">Event Tickets</h1>

              <div className="search-wrapper">
                <input
                  type="text"
                  className="premium-search-input"
                  placeholder="Search events..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            <div className="premium-table-container">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Event Name</th>
                    <th>Total Participants</th>
                    <th className="action-column">View</th>
                    <th className="action-column">Download</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEvents.length > 0 ? (
                    filteredEvents.map((event, index) => {
                      const totalParticipants = participants.filter(
                        (p) => String(p.event_id) === String(event.id)
                      ).length;

                      return (
                        <tr key={event.id}>
                          <td className="index-cell">{index + 1}</td>
                          <td className="fw-500">{event.name}</td>
                          <td>{totalParticipants}</td>

                          <td className="action-column">
                            <button
                              className="premium-btn btn-primary"
                              onClick={() => handleViewListClick(event.id)}
                            >
                              View
                            </button>
                          </td>

                          <td className="action-column">
                            <button
                              className="premium-btn btn-secondary"
                              onClick={() => handleDownloadExcel(event.id)}
                            >
                              Excel
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        No events found for your college.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="premium-report-header detailed-header">
              <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>

                <button
                  onClick={handleBack}
                  className="premium-btn btn-back"
                >
                  <span className="back-arrow">←</span> Back
                </button>

                <h3 className="header-subtitle" style={{ margin: 0 }}>
                  Participants in
                  <span className="highlight-text">
                    {" "}
                    "{events.find((e) => String(e.id) === String(selectedEventId))?.name}"
                  </span>
                </h3>

                {selectedParticipants.length > 0 && (
                  <button 
                    className="premium-btn btn-primary" 
                    style={{ background: '#7c3aed', padding: '6px 12px', fontSize: '14px' }}
                    onClick={handleBulkApprove}
                  >
                    Approve Selected ({selectedParticipants.length})
                  </button>
                )}
              </div>

              <div className="search-wrapper">
                <input
                  type="text"
                  className="premium-search-input"
                  placeholder="Search by Name, Email, Phone, or ID"
                  value={participantSearchText}
                  onChange={(e) => setParticipantSearchText(e.target.value)}
                />
              </div>
            </div>

            <div className="premium-table-container">
              <table className="premium-table detailed-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input 
                        type="checkbox" 
                        onChange={handleSelectAll}
                        checked={selectedParticipants.length > 0 && selectedParticipants.length === filteredParticipants.filter(p => String(p.certificate_approved) !== '1').length}
                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                      />
                    </th>
                    {displayColumns.map((key) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        style={{ cursor: "pointer" }}
                      >
                        {key}
                        {sortColumn === key
                          ? sortOrder === "asc"
                            ? " 🔼"
                            : " 🔽"
                          : ""}
                      </th>
                    ))}
                    <th>Certificate</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredParticipants.length > 0 ? (
                    filteredParticipants.map((p, idx) => (
                      <tr key={idx} style={selectedParticipants.includes(p.id) ? { backgroundColor: '#f3e8ff' } : {}}>
                        <td style={{ textAlign: 'center' }}>
                          {String(p.certificate_approved) !== '1' && (
                            <input 
                              type="checkbox" 
                              checked={selectedParticipants.includes(p.id)}
                              onChange={() => handleSelectParticipant(p.id)}
                              style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                            />
                          )}
                        </td>
                        {displayColumns.map((key) => (
                          <td key={key}>{p[key]}</td>
                        ))}
                        {/* Certificate Approval Action Column */}
                        <td>
                          {String(p.certificate_approved) === '1' ? (
                            <span style={{ color: '#16a34a', fontWeight: 600 }}>✅ Approved</span>
                          ) : (
                            <button
                              className="premium-btn btn-primary"
                              style={{ fontSize: '12px', padding: '5px 10px', background: '#7c3aed' }}
                              onClick={() => handleApproveCertificate(p.id)}
                            >
                              Approve Cert
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="100%" className="empty-state">
                        No participants found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default Eventtickets;